import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';

import { Subscription } from 'rxjs';

import { PieceRateService } from '../../../../services/piece-rate.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { PieceRateAdjSummary } from '@shared/models/piece-rate-summary';

import { calcQuality, calcAllowedBad, calcAfterPenalty } from '../piece-rate-formulas';
import { PieceRateActivity } from '@shared/models/piece-rate-activity';

@Component({
    selector: 'app-piece-rate-group-totals',
    templateUrl: './piece-rate-group-totals.component.html',
    styleUrls: ['./piece-rate-group-totals.component.scss']
})
export class PieceRateGroupTotalsComponent implements OnInit, OnDestroy {
    _activity: PieceRateActivity;

    @Input() set activity(act: PieceRateActivity) {
        this._activity = act;
    }

    get activity(): PieceRateActivity {
        return this._activity;
    }

    groupForm: UntypedFormGroup = new UntypedFormGroup({
        groupPrimaryPieces: new UntypedFormControl(0),
        groupSecondaryPieces: new UntypedFormControl(0),
        groupAudited: new UntypedFormControl(0),
        groupFailed: new UntypedFormControl(0),
    });

    groupQuality: number = 100;
    groupAllowedBad: number = 0;
    groupAfterPenalty: number = 0;
    qualityTarget: number;
    penaltyMultiplier: number;

    showGroupTotals: boolean = false;
    showQuality: boolean = false;
    showSecondaryPieces: boolean = false;

    pieceRateSummary: PieceRateAdjSummary = {};

    subscriptions: Subscription[];

    updateTimeout: { item: {}, timeoutHandle: any } | null = null;

    constructor(private pieceRateService: PieceRateService) { }

    updateCalculations(val: { groupPrimaryPieces: number, groupSecondaryPieces: number, groupAudited: number, groupFailed: number }): void {
        const total = +val.groupPrimaryPieces + (this.showSecondaryPieces ? +val.groupSecondaryPieces : 0);
        this.groupQuality = calcQuality(+val.groupAudited, +val.groupFailed);
        this.groupAllowedBad = calcAllowedBad(total, this.qualityTarget);
        this.groupAfterPenalty = calcAfterPenalty(
            total,
            +val.groupAudited,
            +val.groupFailed,
            this.groupAllowedBad,
            this.penaltyMultiplier);
}

    ngOnInit(): void {
        this.subscriptions = [
            this.groupForm.valueChanges.subscribe(val => {
                this.updateCalculations(val);
            }),

            this.pieceRateService.CurrentPieceRateSummary.subscribe(summary => {
                this.pieceRateSummary = summary;

                this.groupForm.setValue({
                    groupPrimaryPieces: this.pieceRateSummary.groupPrimaryPieces,
                    groupSecondaryPieces: this.pieceRateSummary.groupSecondaryPieces,
                    groupAudited: this.pieceRateSummary.groupAudited,
                    groupFailed: this.pieceRateSummary.groupFailed
                });
            }),

            this.pieceRateService.CurrentPieceRateAdjustment.subscribe(adj => {
                if (adj) {
                    const entry = adj.activityConfigs
                        .find(ae => ae.pieceRateActivity === this._activity.id);

                    this.showGroupTotals = entry?.showGroupTotals ?? false;
                    this.showSecondaryPieces = entry?.showSecondaryCount ?? false;
                    this.showQuality = entry?.showQuality ?? false;
                    this.qualityTarget = entry?.groupQualityTarget ?? 0;
                    this.penaltyMultiplier = entry?.groupPenaltyMultiplier ?? 1;

                    this.updateCalculations(this.groupForm.value);
                }
            })
        ];
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    save(): void {
        if (!this.groupForm.valid) {
            this.groupForm.updateValueAndValidity();
            return;
        }

        const update = {
            groupPrimaryPieces: +this.groupForm.value.groupPrimaryPieces,
            groupSecondaryPieces: +this.groupForm.value.groupSecondaryPieces,
            groupAudited: +this.groupForm.value.groupAudited,
            groupFailed: +this.groupForm.value.groupFailed,
        };


        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout.timeoutHandle);
            this.updateTimeout = null;
        }

        this.updateTimeout = {
            timeoutHandle: setTimeout(() => this.saveImmediate(), 1000),
            item: update
        };
    }

    private saveImmediate(): void {
        const { item } = this.updateTimeout ?? { item: {} };
        this.updateTimeout = null;

        this.pieceRateService.putPieceRateSummary({
            ...this.pieceRateSummary,
            ...item
        });
    }
}
